import { ArrowRightOutlined, LoadingOutlined } from "@ant-design/icons";
import { Breadcrumb, Button, Card, Col, Collapse, Layout, Row, Spin, Tag, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { FC, useEffect, useMemo } from "react";
import { FloatingWhatsApp } from "react-floating-whatsapp";
import { Link } from "react-router-dom";
import supportAvatar from '../assets/support.jpg';
import { IntlButtons } from "../components/IntlButtons";
import { GlobalStore } from "../store/store";
import { formatPrice, getPrice, getProduct } from "../utils";
import { useTranslation } from "react-i18next";
import { useAppLanguage } from "../hooks/useAppLanguage";

const { Content } = Layout;

interface IGoFarmPageProps {
  store: GlobalStore,
};

export const GoFarmPage: FC<IGoFarmPageProps> = observer(({ store }) => {
  const { t } = useTranslation();
  const { currency, language } = useAppLanguage();

  useEffect(() => {
    if (store.products.length === 0) {
      store.getProducts("gofarm");
    }

    store.getIP();
  }, []);

  const products = useMemo(() => {
    return {
      anual: getProduct("anual", store.products),
      trimestral: getProduct("trimestral", store.products),
    };
  }, [store.products]);

  const discount = useMemo(() => {
    const p1 = products["anual"]?.[currency]?.UnitAmount;
    const p2 = products["trimestral"]?.[currency]?.UnitAmount;

    if (!p1 || !p2) {
      return 0;
    }

    return Math.round((1 - (p1 / (p2 * 4))) * 100 as any);
  }, [products, currency]);

  const price_anual = getPrice(products["anual"]?.[currency], currency)
  const price_trimestral = getPrice(products["trimestral"]?.[currency], currency)

  const loading = store.loading || store.loading_ip;

  return (
    <Layout>
      <Content style={{ padding: '0 50px 50px 50px', width: 1150, margin: "auto" }}>
        <Breadcrumb style={{ margin: '16px 0' }}>
          <Breadcrumb.Item>Home</Breadcrumb.Item>
          <Breadcrumb.Item>{t('common.products')}</Breadcrumb.Item>
          <Breadcrumb.Item>GoFarm®</Breadcrumb.Item>
        </Breadcrumb>

        <Row gutter={30} style={{}}>
          <Col md={12} style={{ textAlign: "center" }}>
            <img src="img/gofarm.png" style={{ height: 300 }} alt="GoFarm Logo" />
          </Col>
          <Col md={12}>
            <Card style={{ height: 290 }}>
              <Typography.Title level={3} style={{ marginTop: 0 }}>
                GoFarm®

                <div style={{ float: "right" }}>
                  <IntlButtons />
                </div>
              </Typography.Title>

              <div style={{ height: 10 }} />

              {loading &&
                <Spin indicator={<LoadingOutlined />} />}

              {!loading &&
                <>
                  <Link to={`/gofarm/registro?plano=anual&language=${language}`}>
                    <Card style={{ position: "relative" }}>
                      {(discount >= 3) &&
                        <div style={{ position: "absolute", marginLeft: -8, marginTop: -36, background: "#fff", padding: "0 8px" }}>
                          <Tag bordered={false} color="orange">{discount}% OFF</Tag>
                          {t('common.popular_choice')}
                        </div>}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <span style={{ display: "inline-block", width: 160 }}>
                            {t('common.annual_plan')}
                          </span>
                          <span>
                            {(discount >= 3) ?
                              <>
                                <p style={{ margin: 0, color: "#aaa" }}>
                                  <span style={{ display: "inline-block", width: 44 }}>
                                    {t('common.was')}
                                  </span>
                                  <span style={{ textDecoration: "line-through" }}>
                                    {formatPrice(products["trimestral"]?.[currency]?.UnitAmount! * 4, currency)}
                                  </span>
                                </p>
                                <p style={{ margin: 0 }}>
                                  <span style={{ display: "inline-block", width: 44 }}>
                                    {t('common.now')}
                                  </span>
                                  <span>
                                    {price_anual}
                                  </span>
                                </p>
                              </>
                              :
                              <>
                                <p style={{ margin: 0 }}>
                                  {price_anual}
                                </p>
                              </>}

                          </span>
                        </div>
                        <Button type="primary">
                          <ArrowRightOutlined />
                        </Button>
                      </div>
                    </Card>
                  </Link>

                  <div style={{ height: 10 }} />

                  <Link to={`/gofarm/registro?plano=trimestral&language=${language}`}>
                    <Card>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <span style={{ display: "inline-block", width: 160 }}>
                            {t('common.quarterly_plan')}
                          </span>
                          {price_trimestral} {t('common.price_per_quarter')}
                        </div>
                        <Button type="primary">
                          <ArrowRightOutlined />
                        </Button>
                      </div>
                    </Card>
                  </Link>
                </>}

            </Card>
          </Col>
        </Row>

        <div style={{ height: 40 }} />

        <Collapse
          defaultActiveKey={1}
          size="large"
          items={[
            {
              key: '1',
              label: (
                <strong>
                  {t('common.description')}
                </strong>
              ),
              children: (
                <div>
                  <p>
                    {t('common.gofarm_desc')}
                  </p>
                  <p>
                    <strong>{t('common.benefits')}</strong>
                  </p>
                  <ul>
                    <li>{t('common.benefit_1')}</li>
                    <li>{t('common.benefit_2')}</li>
                    <li>{t('common.benefit_3')}</li>
                    <li>{t('common.benefit_4')}</li>
                  </ul>
                  <p>
                    <strong>{t('common.differentials')}</strong>
                  </p>
                  <ul>
                    <li>{t('common.diff_1')}</li>
                    <li>{t('common.diff_2')}</li>
                    <li>{t('common.diff_3')}</li>
                    <li>{t('common.diff_4')}</li>
                    <li>{t('common.diff_5')}</li>
                  </ul>
                </div>
              )
            }
          ]}
        />

        <FloatingWhatsApp
          accountName="Suporte Tecgraf Agro"
          phoneNumber="+5519999174690"
          avatar={supportAvatar}
          statusMessage={t('common.support_status')}
          chatMessage={t('common.support_message')}
          placeholder={t('common.whatsapp_placeholder')}
        />
      </Content>
    </Layout>
  );
})
